"use client";

/* eslint-disable no-restricted-imports */
import NextLink from "next/link";
import { useCallback } from "react";
import type { ComponentProps } from "react";

import { useRouter } from "./use-router";

// Copied from Next.js source code
function isModifiedEvent(event: React.MouseEvent): boolean {
  const eventTarget = event.currentTarget as HTMLAnchorElement;
  const target = eventTarget.getAttribute("target");
  return (
    (target && target !== "_self") ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey ||
    (event.nativeEvent && (event.nativeEvent as MouseEvent).button === 1)
  );
}

// Copied from Next.js source code
function shouldPreserveDefault(e: React.MouseEvent): boolean {
  const { nodeName } = e.currentTarget;
  const isAnchorNodeName = nodeName.toUpperCase() === "A";

  if (isAnchorNodeName && isModifiedEvent(e)) {
    return true;
  }

  return false;
}

export type LinkProps = ComponentProps<typeof NextLink>;

/**
 * Link component that triggers view transitions on navigation.
 * This is a wrapper around next/link that explicitly uses the router APIs.
 */
function hrefToString(href: LinkProps["href"]): string {
  if (typeof href === "string") return href;

  const pathname = href.pathname ?? "";
  const { query } = href;

  if (!query) return pathname;

  if (typeof query === "string") {
    return query ? `${pathname}?${query}` : pathname;
  }

  const params = new URLSearchParams(
    Object.entries(query).reduce(
      (acc, [key, val]) => {
        if (val !== undefined && val !== null) {
          acc[key] = String(val);
        }
        return acc;
      },
      {} as Record<string, string>
    )
  ).toString();

  return params ? `${pathname}?${params}` : pathname;
}

export function Link(props: LinkProps) {
  const router = useRouter();
  const {
    href,
    as,
    replace,
    scroll,
    onClick: onClickProp,
    ...restProps
  } = props;

  const onClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (onClickProp) {
        onClickProp(e);
      }

      if ("startViewTransition" in document) {
        if (shouldPreserveDefault(e)) {
          return;
        }

        e.preventDefault();
        const navigate = replace ? router.replace : router.push;
        navigate(as ? hrefToString(as) : hrefToString(href), {
          scroll: scroll != null ? scroll : true,
        });
      }
    },
    [onClickProp, href, as, replace, scroll, router]
  );

  return (
    <NextLink
      {...restProps}
      href={href}
      as={as}
      replace={replace}
      scroll={scroll}
      onClick={onClick}
    />
  );
}
